import React, { useState, useEffect } from "react";
import {
  Database,
  FileText,
  AlertTriangle,
  Shield,
  CheckSquare,
  FileSearch,
  Users,
  TrendingUp,
  Loader2,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ArrowRight,
  Rocket,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// COMPOSANT DASHBOARDSTATS
// Affiche les statistiques de conformité Loi 25
// ============================================

const DashboardStats = ({ entrepriseId, onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger toutes les stats
  const loadStats = async () => {
    if (!entrepriseId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = {
        inventaire: { total: 0, conforme: 0, aReviser: 0, nonConforme: 0 },
        documents: { total: 0, approuves: 0, brouillons: 0 },
        incidents: {
          total: 0,
          nouveaux: 0,
          enCours: 0,
          resolus: 0,
          prejudiceSerieux: 0,
          caiRequise: 0,
        },
        efvp: {
          total: 0,
          enCours: 0,
          termines: 0,
          horsQuebec: 0,
          risqueEleve: 0,
        },
        consentements: {
          total: 0,
          actifs: 0,
          inactifs: 0,
          explicites: 0,
          aRenouveler: 0,
        },
        taches: { total: 0, enRetard: 0, aVenir: 0 },
      };

      // 1. INVENTAIRE DES DONNÉES
      try {
        const { data: inventaire, error: invErr } = await supabase
          .from("inventaire_donnees")
          .select(
            "id, sensibilite, responsable, finalites, localisation, securite, minimisation_validee, acces_limite"
          )
          .eq("entreprise_id", entrepriseId);

        if (!invErr && inventaire) {
          result.inventaire.total = inventaire.length;

          // Évaluer la conformité de chaque item
          inventaire.forEach((item) => {
            const hasRequired = !!(
              item.responsable &&
              item.finalites &&
              item.localisation
            );
            const hasSecurity =
              Array.isArray(item.securite) && item.securite.length > 0;

            if (
              hasRequired &&
              hasSecurity &&
              item.minimisation_validee &&
              item.acces_limite
            ) {
              result.inventaire.conforme++;
            } else if (hasRequired && hasSecurity) {
              result.inventaire.aReviser++;
            } else {
              result.inventaire.nonConforme++;
            }
          });
        }
      } catch (e) {
        console.warn("Erreur chargement inventaire:", e);
      }

      // 2. DOCUMENTS
      try {
        const { data: documents, error: docErr } = await supabase
          .from("documents")
          .select("id, statut")
          .eq("entreprise_id", entrepriseId);

        if (!docErr && documents) {
          result.documents.total = documents.length;
          result.documents.approuves = documents.filter(
            (d) => d.statut === "Approuvé"
          ).length;
          result.documents.brouillons = documents.filter(
            (d) => d.statut === "Brouillon" || !d.statut
          ).length;
        }
      } catch (e) {
        console.warn("Erreur chargement documents:", e);
      }

      // 3. INCIDENTS (Art. 3.5)
      try {
        const { data: incidents, error: incErr } = await supabase
          .from("incidents")
          .select(
            "id, statut, severite, prejudice_serieux, cai_notifiee, cai_requise"
          )
          .eq("entreprise_id", entrepriseId);

        if (!incErr && incidents) {
          result.incidents.total = incidents.length;
          result.incidents.nouveaux = incidents.filter(
            (i) => i.statut === "Nouveau"
          ).length;
          result.incidents.enCours = incidents.filter((i) =>
            ["En analyse", "En cours", "En attente"].includes(i.statut)
          ).length;
          result.incidents.resolus = incidents.filter(
            (i) => i.statut === "Résolu" || i.statut === "Fermé"
          ).length;
          result.incidents.prejudiceSerieux = incidents.filter(
            (i) => i.prejudice_serieux
          ).length;
          result.incidents.caiRequise = incidents.filter(
            (i) => i.cai_requise && !i.cai_notifiee
          ).length;
        }
      } catch (e) {
        console.warn("Erreur chargement incidents:", e);
      }

      // 4. EFVP
      try {
        const { data: efvp, error: efvpErr } = await supabase
          .from("efvp")
          .select(
            "id, statut, progression, niveau_risque_global, communication_hors_quebec"
          )
          .eq("entreprise_id", entrepriseId);

        if (!efvpErr && efvp) {
          result.efvp.total = efvp.length;

          // Classification EXCLUSIVE (chaque EFVP compte une seule fois)
          efvp.forEach((e) => {
            // Terminée si statut "Terminé"/"Approuvé" OU progression 100%
            if (
              e.statut === "Terminé" ||
              e.statut === "Approuvé" ||
              e.progression === 100
            ) {
              result.efvp.termines++;
            }
            // En cours si statut "En cours"/"En révision" ET pas encore à 100%
            else if (e.statut === "En cours" || e.statut === "En révision") {
              result.efvp.enCours++;
            }
            // Sinon c'est "Non démarré" (pas compté séparément)
          });

          // Stats additionnelles (peuvent se cumuler)
          result.efvp.horsQuebec = efvp.filter(
            (e) => e.communication_hors_quebec
          ).length;
          result.efvp.risqueEleve = efvp.filter(
            (e) =>
              e.niveau_risque_global === "Élevé" ||
              e.niveau_risque_global === "Critique"
          ).length;
        }
      } catch (e) {
        console.warn("Erreur chargement EFVP:", e);
      }

      // 5. CONSENTEMENTS (Art. 14)
      try {
        const { data: consentements, error: consErr } = await supabase
          .from("consentements")
          .select(
            "id, actif, consentement_explicite, donnees_sensibles, date_expiration"
          )
          .eq("entreprise_id", entrepriseId);

        if (!consErr && consentements) {
          result.consentements.total = consentements.length;
          result.consentements.actifs = consentements.filter(
            (c) => c.actif
          ).length;
          result.consentements.inactifs = consentements.filter(
            (c) => !c.actif
          ).length;
          result.consentements.explicites = consentements.filter(
            (c) => c.consentement_explicite
          ).length;

          // Consentements expirant dans les 30 jours
          const now = new Date();
          result.consentements.aRenouveler = consentements.filter((c) => {
            if (!c.date_expiration) return false;
            const exp = new Date(c.date_expiration);
            const diff = (exp - now) / (1000 * 60 * 60 * 24);
            return diff <= 30 && diff > 0;
          }).length;
        }
      } catch (e) {
        console.warn("Erreur chargement consentements:", e);
      }

      setStats(result);
    } catch (err) {
      console.error("Erreur chargement stats:", err);
      setError("Impossible de charger les statistiques");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [entrepriseId]);

  // ============================================
  // CALCUL DU SCORE DE CONFORMITÉ GLOBAL (sur 100)
  // ============================================
  const calculateConformityScore = () => {
    if (!stats) return 0;

    // Compte vide → 0 % : rien n'a encore été démarré
    const totalElements =
      stats.inventaire.total +
      stats.documents.total +
      stats.incidents.total +
      stats.efvp.total +
      stats.consentements.total;
    if (totalElements === 0) return 0;

    // Dénominateur FIXE de 100 points réparti sur les 5 modules.
    // (L'ancien dénominateur variable donnait 20/40 = 50 % à vide.)
    let score = 0;

    // Inventaire — 30 pts
    if (stats.inventaire.total > 0) {
      score += (stats.inventaire.conforme / stats.inventaire.total) * 30;
    }
    // Documents — 20 pts
    if (stats.documents.total > 0) {
      score += (stats.documents.approuves / stats.documents.total) * 20;
    }
    // Incidents — 20 pts : pleine note si aucun incident OU tous résolus
    if (stats.incidents.total > 0) {
      score += (stats.incidents.resolus / stats.incidents.total) * 20;
    } else {
      score += 20; // gestion en règle (le compte est déjà actif)
    }
    // EFVP — 15 pts
    if (stats.efvp.total > 0) {
      score += (stats.efvp.termines / stats.efvp.total) * 15;
    }
    // Consentements — 15 pts
    if (stats.consentements.total > 0) {
      score += (stats.consentements.actifs / stats.consentements.total) * 15;
    }

    return Math.round(score);
  };

  const conformityScore = calculateConformityScore();

  // Le compte a-t-il des données dans au moins un module ?
  const hasData =
    !!stats &&
    stats.inventaire.total +
      stats.documents.total +
      stats.incidents.total +
      stats.efvp.total +
      stats.consentements.total >
      0;

  // Déterminer le statut global (statuts existants + "À démarrer")
  const getGlobalStatus = () => {
    if (!hasData) return { label: "À démarrer", color: "slate", icon: Rocket };
    if (conformityScore >= 80)
      return { label: "Conforme", color: "green", icon: CheckCircle };
    if (conformityScore >= 50)
      return { label: "En progression", color: "amber", icon: Clock };
    return { label: "À risque", color: "red", icon: AlertCircle };
  };

  const globalStatus = getGlobalStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={48} className="text-sky-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
        <AlertTriangle className="text-red-500 mx-auto mb-3" size={40} />
        <p className="text-red-700 dark:text-red-300">{error}</p>
        <button
          onClick={loadStats}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Score de conformité global */}
      <div
        className={`bg-gradient-to-r ${
          globalStatus.color === "green"
            ? "from-green-500 to-emerald-600"
            : globalStatus.color === "amber"
            ? "from-amber-500 to-orange-600"
            : globalStatus.color === "slate"
            ? "from-slate-500 to-slate-600"
            : "from-red-500 to-rose-600"
        } rounded-2xl p-6 text-white shadow-xl`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm font-medium">
              Score de conformité Loi 25
            </p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-5xl font-bold">{conformityScore}%</span>
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                <globalStatus.icon size={18} />
                <span className="font-medium">{globalStatus.label}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <button
              onClick={loadStats}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition"
              title="Actualiser"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mt-4 h-3 bg-white/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${conformityScore}%` }}
          />
        </div>
      </div>

      {/* Message d'accueil pour un compte qui démarre */}
      {!hasData && (
        <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-5">
          <h3 className="font-semibold text-sky-800 dark:text-sky-300 mb-2 flex items-center gap-2">
            <Rocket size={20} />
            Bienvenue ! Démarrons votre conformité
          </h3>
          <p className="text-sm text-sky-700 dark:text-sky-300">
            Votre score grimpera à mesure que vous compléterez vos modules.
            Commencez par inventorier vos renseignements personnels, puis créez
            votre politique de confidentialité.
          </p>
        </div>
      )}

      {/* Cartes statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Inventaire */}
        <StatCard
          icon={Database}
          title="Inventaire des données"
          total={stats.inventaire.total}
          color="sky"
          details={[
            {
              label: "Conforme",
              value: stats.inventaire.conforme,
              color: "green",
            },
            {
              label: "À réviser",
              value: stats.inventaire.aReviser,
              color: "amber",
            },
            {
              label: "Non conforme",
              value: stats.inventaire.nonConforme,
              color: "red",
            },
          ]}
        />

        {/* Documents */}
        <StatCard
          icon={FileText}
          title="Documents"
          total={stats.documents.total}
          color="indigo"
          details={[
            {
              label: "Approuvés",
              value: stats.documents.approuves,
              color: "green",
            },
            {
              label: "Brouillons",
              value: stats.documents.brouillons,
              color: "gray",
            },
          ]}
        />

        {/* Incidents */}
        <StatCard
          icon={AlertTriangle}
          title="Incidents"
          total={stats.incidents.total}
          color="red"
          details={[
            {
              label: "Nouveaux",
              value: stats.incidents.nouveaux,
              color: "blue",
            },
            {
              label: "En cours",
              value: stats.incidents.enCours,
              color: "amber",
            },
            {
              label: "Résolus",
              value: stats.incidents.resolus,
              color: "green",
            },
            {
              label: "Préjudice sérieux",
              value: stats.incidents.prejudiceSerieux,
              color: "red",
            },
          ]}
        />

        {/* EFVP */}
        <StatCard
          icon={FileSearch}
          title="EFVP"
          total={stats.efvp.total}
          color="purple"
          details={[
            { label: "En cours", value: stats.efvp.enCours, color: "amber" },
            { label: "Terminées", value: stats.efvp.termines, color: "green" },
            { label: "Hors QC", value: stats.efvp.horsQuebec, color: "blue" },
            {
              label: "Risque élevé",
              value: stats.efvp.risqueEleve,
              color: "red",
            },
          ]}
        />

        {/* Consentements */}
        <StatCard
          icon={CheckSquare}
          title="Consentements"
          total={stats.consentements.total}
          color="emerald"
          details={[
            {
              label: "Actifs",
              value: stats.consentements.actifs,
              color: "green",
            },
            {
              label: "Explicites",
              value: stats.consentements.explicites,
              color: "blue",
            },
            {
              label: "À renouveler",
              value: stats.consentements.aRenouveler,
              color: "amber",
            },
          ]}
        />

        {/* Actions requises */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border-l-4 border-amber-500">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Clock className="text-amber-600 dark:text-amber-400" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Actions requises
              </h3>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            {stats.incidents.caiRequise > 0 && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium">
                <AlertTriangle size={16} />
                <span>
                  ⚠️ {stats.incidents.caiRequise} incident(s) à notifier à la
                  CAI
                </span>
              </div>
            )}
            {stats.inventaire.nonConforme > 0 && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <XCircle size={16} />
                <span>
                  {stats.inventaire.nonConforme} donnée(s) non conforme(s)
                </span>
              </div>
            )}
            {stats.incidents.nouveaux > 0 && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertCircle size={16} />
                <span>{stats.incidents.nouveaux} incident(s) à traiter</span>
              </div>
            )}
            {stats.consentements.aRenouveler > 0 && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <Clock size={16} />
                <span>
                  {stats.consentements.aRenouveler} consentement(s) à renouveler
                </span>
              </div>
            )}
            {stats.documents.brouillons > 0 && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <FileText size={16} />
                <span>
                  {stats.documents.brouillons} document(s) en brouillon
                </span>
              </div>
            )}
            {stats.incidents.caiRequise === 0 &&
              stats.inventaire.nonConforme === 0 &&
              stats.incidents.nouveaux === 0 &&
              stats.consentements.aRenouveler === 0 &&
              stats.documents.brouillons === 0 && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle size={16} />
                  <span>Aucune action urgente</span>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Conseils de conformité */}
      {conformityScore < 80 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
          <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
            <TrendingUp size={20} />
            Conseils pour améliorer votre conformité
          </h3>
          <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
            {stats.inventaire.total === 0 && (
              <li className="flex items-center gap-2">
                <ArrowRight size={16} />
                Commencez par inventorier vos renseignements personnels
              </li>
            )}
            {stats.inventaire.nonConforme > 0 && (
              <li className="flex items-center gap-2">
                <ArrowRight size={16} />
                Complétez les informations manquantes dans l'inventaire (
                {stats.inventaire.nonConforme} items)
              </li>
            )}
            {stats.documents.total === 0 && (
              <li className="flex items-center gap-2">
                <ArrowRight size={16} />
                Créez votre politique de confidentialité
              </li>
            )}
            {stats.consentements.total === 0 && (
              <li className="flex items-center gap-2">
                <ArrowRight size={16} />
                Configurez vos formulaires de consentement
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT STAT CARD
// ============================================
const StatCard = ({ icon: Icon, title, total, color, details }) => {
  const colorClasses = {
    sky: "border-sky-500 bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400",
    indigo:
      "border-indigo-500 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400",
    red: "border-red-500 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
    purple:
      "border-purple-500 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400",
    emerald:
      "border-emerald-500 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400",
  };

  const detailColors = {
    green: "text-green-600 dark:text-green-400",
    amber: "text-amber-600 dark:text-amber-400",
    red: "text-red-600 dark:text-red-400",
    blue: "text-blue-600 dark:text-blue-400",
    gray: "text-gray-500 dark:text-gray-400",
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border-l-4 ${
        colorClasses[color]?.split(" ")[0] || "border-gray-500"
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`p-2 rounded-lg ${
            colorClasses[color]?.split(" ").slice(1, 3).join(" ") ||
            "bg-gray-100"
          }`}
        >
          <Icon
            className={
              colorClasses[color]?.split(" ").slice(3).join(" ") ||
              "text-gray-600"
            }
            size={24}
          />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {total}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </div>

      {details && details.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          {details.map((detail, idx) => (
            <span
              key={idx}
              className={detailColors[detail.color] || "text-gray-600"}
            >
              {detail.value} {detail.label.toLowerCase()}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardStats;
